/**
 * DelayedButton — fills up over `delayMs`, then becomes clickable.
 *
 * Use this for any pop-up where the player must read the text first:
 *   - Sai activations (current usage)
 *   - "Сай узнала тебя" mid-game scene (docs/18)
 *   - Crystallization cinematic, if/when we add one
 *   - Future onboarding milestones (docs/13)
 *
 * Behavior:
 *   - Mounts with `fillPercent = 0`, button disabled (visual + actual).
 *   - rAF loop animates `--fill-percent` CSS var 0 → 100 over `delayMs`.
 *   - At 100 the button switches to ready state (gold border, cursor
 *     pointer) and the `onClick` becomes effective.
 *   - Honors `prefers-reduced-motion`: skips visual fill but STILL
 *     enforces the delay so the read-time guarantee holds. (Motion ≠
 *     comprehension time.)
 *   - Cleans up the rAF on unmount.
 *
 * Props:
 *   - `onClick`: fires after delay completes AND user taps.
 *   - `delayMs`: defaults to 1000.
 *   - `children`: button label.
 *   - `ariaLabel`: optional override for accessibility.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './DelayedButton.module.css';

interface DelayedButtonProps {
  readonly onClick: () => void;
  readonly delayMs?: number;
  readonly children: React.ReactNode;
  readonly ariaLabel?: string;
}

export function DelayedButton({
  onClick,
  delayMs = 1000,
  children,
  ariaLabel,
}: DelayedButtonProps): JSX.Element {
  const [ready, setReady] = useState(false);
  const [fillPct, setFillPct] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startedAtRef = useRef<number | null>(null);

  // Start the fill animation when the component mounts.
  // (One-shot; the button never "re-fills" on its own.)
  useEffect(() => {
    startedAtRef.current = performance.now();

    const tick = (now: number): void => {
      const startedAt = startedAtRef.current;
      if (startedAt === null) return;
      const elapsed = now - startedAt;
      const pct = Math.min(100, (elapsed / delayMs) * 100);
      setFillPct(pct);
      if (pct >= 100) {
        setReady(true);
        rafRef.current = null;
        return;
      }
      rafRef.current = window.requestAnimationFrame(tick);
    };
    rafRef.current = window.requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, [delayMs]);

  const handleClick = useCallback((): void => {
    if (!ready) return; // double-guard the disabled state
    onClick();
  }, [ready, onClick]);

  const className = ready ? `${styles.btn ?? ''} ${styles.btnReady ?? ''}` : (styles.btn ?? '');

  return (
    <button
      type="button"
      className={className}
      onClick={handleClick}
      disabled={!ready}
      aria-label={ariaLabel}
      aria-disabled={!ready}
      style={{ ['--fill-percent' as string]: `${fillPct.toString()}%` }}
    >
      <span className={styles.label}>{children}</span>
    </button>
  );
}
